import { COULEURS, type Couleur } from '@nextilt/commun';
import type { ChampsALaNaissance } from '../partage/api/types';

/**
 * Le + du téléphone selon la page (chantier `telephone-projets-fiche`, ticket 05, décisions
 * d'Igor du 2026-10-05, revue 20 et 21, cadrage 9, 14 et 17 ; maquette `design/telephone.html`,
 * « Le + dans l'Agenda » et « Nouveau projet »). Sans rendu.
 *
 * Le + ajoute là où l'on est : dans Tous les projets, un nouveau projet ; dans un projet ou une
 * étape, une Tâche ou une Idée dedans ; dans Journée, une tâche de plus dans la journée du jour ;
 * dans l'Agenda, une Tâche (qui entre dans la journée de ce jour), un Rendez-vous ou une
 * Échéance sur le jour touché ; dans Maintenant, en attendant la décision 17, une tâche dans un
 * projet, sans plus. Ni Rappel, ni choix d'une étape. Hors ligne, Tâche et Idée seulement.
 */

export type PageDuPlus =
  | { sorte: 'projets' }
  | { sorte: 'feuille'; id: string }
  | { sorte: 'journee' }
  /** `jour` : le jour ouvert, sinon celui touché dans le mois ; `projet` : celui qui filtre l'Agenda. */
  | { sorte: 'agenda'; jour: string | null; projet: string | null }
  | { sorte: 'maintenant' };

export type SorteDuPlus = 'tache' | 'idee' | 'rendez_vous' | 'echeance';

/** La page d'après l'adresse ; toute autre adresse est Maintenant, où l'app s'ouvre. */
export function pageDuPlus(chemin: string, recherche: string): PageDuPlus {
  if (chemin === '/dossiers') return { sorte: 'projets' };
  const feuille = /^\/feuille\/([^/]+)/.exec(chemin);
  if (feuille?.[1]) return { sorte: 'feuille', id: decodeURIComponent(feuille[1]) };
  if (chemin === '/journee') return { sorte: 'journee' };
  if (chemin === '/agenda') {
    const p = new URLSearchParams(recherche);
    // Le jour touché ne vit que dans le mois (`AgendaAuPouce`, `aller`).
    const touche = p.get('vue') === 'mois' ? p.get('touche') : null;
    return { sorte: 'agenda', jour: p.get('jour') ?? touche, projet: p.get('projet') };
  }
  return { sorte: 'maintenant' };
}

/**
 * La feuille qui monte : le nouveau projet dans Tous les projets, et partout où il faudrait un
 * projet pour y mettre quelque chose quand il n'y en a encore aucun. Une feuille ouverte a déjà
 * où ranger.
 */
export function feuilleDuPlus(page: PageDuPlus, aDesProjets: boolean): 'nouveau-projet' | 'ajout' {
  if (page.sorte === 'projets') return 'nouveau-projet';
  if (page.sorte === 'feuille') return 'ajout';
  return aDesProjets ? 'ajout' : 'nouveau-projet';
}

/** Ce qu'on peut ajouter, dans l'ordre ; hors ligne, seules Tâche et Idée passent par la file. */
export function sortesDuPlus(page: PageDuPlus, enLigne: boolean): { sorte: SorteDuPlus; possible: boolean }[] {
  const sortes: SorteDuPlus[] =
    page.sorte === 'feuille'
      ? ['tache', 'idee']
      : page.sorte === 'agenda'
        ? ['tache', 'rendez_vous', 'echeance']
        : page.sorte === 'projets'
          ? []
          : ['tache'];
  return sortes.map((sorte) => ({ sorte, possible: enLigne || sorte === 'tache' || sorte === 'idee' }));
}

/** Le jour de l'Agenda : le jour touché, aujourd'hui sans jour touché ou pour un jour passé. */
export function jourDuPlus(page: PageDuPlus, aujourdhui: string): string | null {
  if (page.sorte !== 'agenda') return null;
  return page.jour && page.jour >= aujourdhui ? page.jour : aujourdhui;
}

/** La journée où la Tâche entre : celle d'aujourd'hui dans Journée, celle du jour dans l'Agenda. */
export function jourDeLaJournee(page: PageDuPlus, sorte: SorteDuPlus, aujourdhui: string): string | null {
  if (sorte !== 'tache') return null;
  if (page.sorte === 'journee') return aujourdhui;
  return jourDuPlus(page, aujourdhui);
}

/**
 * Le projet proposé (le ▾), hors d'une feuille : celui qui filtre l'Agenda, sinon le dernier où
 * le + a mis quelque chose, sinon le premier projet. Un projet qui n'existe plus ne compte pas.
 */
export function projetDuPlus(page: PageDuPlus, dernier: string | null, projets: readonly string[]): string | null {
  const existe = (id: string | null): id is string => !!id && projets.includes(id);
  if (page.sorte === 'agenda' && existe(page.projet)) return page.projet;
  if (existe(dernier)) return dernier;
  return projets[0] ?? null;
}

/** Le projet d'une feuille : le début de son chemin (la racine, elle comprise). */
export function racineDeLaFeuille(feuille: { chemin: readonly { id: string }[]; bulle: { id: string } }): string {
  return feuille.chemin[0]?.id ?? feuille.bulle.id;
}

/**
 * La couleur proposée à un nouveau projet, parmi les cinq de `COULEURS` (`@nextilt/commun`,
 * `--dossier-1` à `--dossier-5`) : la moins portée par les projets, la première à égalité.
 */
export function couleurProposee(couleurs: readonly (string | null)[]): Couleur {
  const combien = (c: string) => couleurs.filter((x) => x === c).length;
  return COULEURS.reduce((meilleure, c) => (combien(c) < combien(meilleure) ? c : meilleure));
}

/** « 2026-10-09 » et « 14:30 » : l'instant, à l'heure du téléphone, comme la fiche le pose. */
export function instantDuRendezVous(jour: string, heure: string): string | null {
  if (!/^\d{2}:\d{2}$/.test(heure)) return null;
  const instant = new Date(`${jour}T${heure}`);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

/** Ce avec quoi la bulle naît, selon ce que c'est : envoyé dans la demande qui la crée. */
export function champsDeLaSorte(sorte: SorteDuPlus, jour: string | null, heure: string): ChampsALaNaissance | null {
  switch (sorte) {
    case 'idee':
      return { etat: 'sans_objet' };
    case 'echeance':
      return jour ? { dateEcheance: jour } : null;
    case 'rendez_vous': {
      const debut = jour ? instantDuRendezVous(jour, heure) : null;
      return debut ? { debut } : null;
    }
    default:
      return null;
  }
}

/** Un titre, et l'heure pour un rendez-vous : seuls les champs utiles, mais ceux-là. */
export function peutAjouter(sorte: SorteDuPlus, titre: string, heure: string): boolean {
  if (!titre.trim()) return false;
  return sorte !== 'rendez_vous' || /^\d{2}:\d{2}$/.test(heure);
}
